import { useId, useState } from "react";
import { api, formatError } from "@/lib/api";
import { direccionVacia } from "@/lib/registro";
import AddressFields, { AvisoTablas } from "@/components/registry/AddressFields";
import { useRegistryRefdata } from "@/components/registry/useRegistryRefdata";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const EN_OFICINA = { at_establishment: true, address: null };
// Lo guardado (o nada: en la oficina). En la oficina no se guarda otra dirección (la API la descarta).
const lugar = (v) => (v && typeof v === "object" && v.at_establishment === false
  ? { at_establishment: false, address: v.address || null } : EN_OFICINA);

function Lugar({ titulo, value, onChange, refdata, testid }) {
  const uid = useId();
  return (
    <fieldset className="min-w-0 space-y-2" data-testid={testid}>
      <legend className="font-semibold text-[#1A1A1A] mb-1">{titulo}</legend>
      <div className="flex items-center gap-2 text-sm">
        <Checkbox id={`${uid}-oficina`} checked={value.at_establishment} data-testid={`${testid}-office`}
          onCheckedChange={(c) => onChange(c === true ? EN_OFICINA
            : { at_establishment: false, address: value.address || direccionVacia() })} />
        <Label htmlFor={`${uid}-oficina`}>En la oficina de la reserva</Label>
      </div>
      {!value.at_establishment && (
        <AddressFields value={value.address || direccionVacia()} onChange={(a) => onChange({ ...value, address: a })}
          refdata={refdata} testid={`${testid}-addr`} avisarTablas={false} autoCompletar={false} />
      )}
    </fieldset>
  );
}

// Dónde se entrega y se devuelve el vehículo, para el registro de viajeros: en la oficina de la reserva o en otra
// dirección. No es la dirección de quien escribe: sin autocompletar. Un solo aviso si no llegan las tablas.
// Tras un 409 (el registro se ha desactivado mientras tanto) se vuelve a leer la ficha (`onStale`).
export default function RegistryPlacesEditor({ reservationId, data, onClose, onSaved, onStale }) {
  const refdata = useRegistryRefdata();
  const [recogida, setRecogida] = useState(() => lugar(data.places?.pickup));
  const [devolucion, setDevolucion] = useState(() => lugar(data.places?.return));
  const [guardando, setGuardando] = useState(false);
  const guardar = async () => {
    setGuardando(true);
    try {
      const { data: v } = await api.put(`/reservations/${reservationId}/registry/places`,
        { pickup: recogida, return: devolucion });
      toast.success("Lugares guardados");
      onSaved(v);
    } catch (err) {
      toast.error(formatError(err));
      if (err?.response?.status === 409) onStale?.();
    } finally {
      setGuardando(false);
    }
  };
  return (
    <Dialog open onOpenChange={(abierto) => { if (!abierto) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Lugares de entrega y devolución</DialogTitle>
          <DialogDescription className="text-xs text-[#5C5C5C]">
            Dónde se entrega y se devuelve el vehículo, para el registro de viajeros.
          </DialogDescription>
        </DialogHeader>
        <AvisoTablas refdata={refdata} />
        <Lugar titulo="Entrega del vehículo" value={recogida} onChange={setRecogida} refdata={refdata} testid="rp-pickup" />
        <Lugar titulo="Devolución" value={devolucion} onChange={setDevolucion} refdata={refdata} testid="rp-return" />
        <div className="flex justify-end">
          <Button type="button" onClick={guardar} disabled={guardando} className="bg-marca hover:bg-marca-hover text-white"
            data-testid="rp-save">
            {guardando ? "Guardando…" : "Guardar"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
